import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import {
  OTHER_FAMILY,
  familyOfTags,
  familyOfTemplateName,
  isResourceOperationTemplate,
  isStackOperationTemplate,
} from '@internal/plugin-stack-common';
import type { StackOperation } from '@internal/plugin-stack-common';
import type { AsyncState } from './stacks';

export type TemplateView = {
  key: string;
  name: string;
  title: string;
  description?: string;
  namespace: string;
  /**
   * The tenant this serving of the template belongs to.
   *
   * The IDP generates one Template entity per tenant per template and puts the
   * tenant in the entity's namespace, so a user entitled to three tenants is
   * offered three entities that differ in nothing a menu shows: same title,
   * same name, same tags. Undefined for the handful of templates the app ships
   * itself or test fixtures may live in `default` and belong to no tenant.
   */
  tenant?: string;
  tags: string[];
  /** Route to the scaffolder form. */
  path: string;
  /** `azure`, `cloudstack`, or undefined for a template with no substrate tag. */
  substrate?: string;
  /**
   * True when the template acts on a stack that already exists (power actions,
   * reconfigure, update). Those belong on the stack's own page, not in a menu
   * that reads as "make me a new thing".
   */
  isDayTwo: boolean;
  /** Typed Stack lifecycle taxonomy. Undefined for non-Stack Templates. */
  stackType?: string;
  /** Typed non-Stack lifecycle target emitted by Orchestrator. */
  resourceType?: string;
  operation?: StackOperation;
  familyId: string;
  familyLabel: string;
};

const SUBSTRATES = ['cloudstack', 'azure'];

/** Where a template that belongs to no tenant lands. */
const DEFAULT_NAMESPACE = 'default';

/**
 * Tags that mean "this acts on something that already exists".
 *
 * Typed control-plane Templates declare their operation explicitly. Untyped
 * third-party Templates fall back to tags for compatibility.
 */
const DAY_TWO_TAGS = ['day-2', 'destroy', 'delete'];

/**
 * Untyped third-party Templates may also use a day-2 name suffix. It is a
 * naming convention rather than a contract, so it remains the last resort.
 */
const DAY_TWO_SUFFIX = /-(update|delete|destroy)$/;

export function stackOperationOf(
  entity: Entity,
): { stackType: string; operation: StackOperation } | undefined {
  if (!isStackOperationTemplate(entity)) {
    return undefined;
  }
  return {
    stackType: entity.spec.stackType,
    operation: entity.spec.operation,
  };
}

export function resourceOperationOf(
  entity: Entity,
): { resourceType: string; operation: StackOperation } | undefined {
  if (!isResourceOperationTemplate(entity)) {
    return undefined;
  }
  return {
    resourceType: entity.spec.resourceType,
    operation: entity.spec.operation,
  };
}

const operationOf = (entity: Entity) =>
  stackOperationOf(entity) ?? resourceOperationOf(entity);

/** What each Stack operation is called where a user confirms it. */
const OPERATION_LABELS: Record<StackOperation, string> = {
  create: 'Create',
  update: 'Update',
  action: 'Run',
  delete: 'Destroy',
};

/**
 * The verb that names what submitting a template's form will do (#14005).
 *
 * The wizard is one form for every operation, so without this every one of
 * them confirms with "Create" - including the destroy form, where the button
 * sits under a title that says the VM is about to be torn down.
 *
 * Read from the typed taxonomy first, exactly like `isDayTwo`. App-owned
 * non-Stack Templates carry no taxonomy, so they fall back to the same name
 * suffix convention, and anything else is a creation - which is what every
 * template that says nothing about itself is.
 */
export const operationLabel = (entity: Entity): string => {
  const taxonomy = operationOf(entity);
  if (taxonomy) return OPERATION_LABELS[taxonomy.operation];

  const name = entity.metadata.name.toLowerCase();
  if (/-(delete|destroy)$/.test(name)) return OPERATION_LABELS.delete;
  if (/-update$/.test(name)) return OPERATION_LABELS.update;
  return OPERATION_LABELS.create;
};

const isDayTwoTemplate = (
  entity: Entity,
  tags: string[],
  taxonomy: ReturnType<typeof operationOf>,
): boolean => {
  if (taxonomy) return taxonomy.operation !== 'create';

  // This inference applies only to untyped third-party entities. Typed DSO
  // Templates always use their explicit operation.
  return (
    tags.some(tag => DAY_TWO_TAGS.includes(tag.toLowerCase())) ||
    DAY_TWO_SUFFIX.test(entity.metadata.name.toLowerCase())
  );
};

export const toTemplateView = (entity: Entity): TemplateView => {
  const namespace = entity.metadata.namespace ?? DEFAULT_NAMESPACE;
  // Deduplicated once here rather than at each use: nothing stops a template
  // from listing the same tag twice, and a repeated tag renders as a repeated
  // chip and as a duplicate React key in every list that draws one.
  const tags = [...new Set(entity.metadata.tags ?? [])];
  const taxonomy = operationOf(entity);
  // Families are derived from the template's own tags rather than listed here,
  // because the IDP ingests new templates into the catalog without anybody
  // editing this app. The table lives in stack-common so that what a family is
  // called in this menu and what its entity page is allowed to offer cannot
  // drift apart.
  // Policy workflows are Catalog Templates, not Stack families. Give them a
  // discoverable menu group without adding fake Stack capabilities.
  const family =
    entity.metadata.name === 'application-group-policy' ||
    entity.metadata.name === 'application-group-quota'
      ? { id: 'governance', label: 'Policies and quotas' }
      : (taxonomy && 'stackType' in taxonomy
          ? familyOfTemplateName(taxonomy.stackType)
          : undefined) ?? familyOfTags(tags);

  return {
    key: entity.metadata.uid ?? `${namespace}/${entity.metadata.name}`,
    name: entity.metadata.name,
    title: entity.metadata.title ?? entity.metadata.name,
    description: entity.metadata.description,
    namespace,
    tenant: namespace === DEFAULT_NAMESPACE ? undefined : namespace,
    tags,
    path: `/create/templates/${namespace}/${entity.metadata.name}`,
    substrate: tags.find(tag => SUBSTRATES.includes(tag.toLowerCase())),
    isDayTwo: isDayTwoTemplate(entity, tags, taxonomy),
    stackType:
      taxonomy && 'stackType' in taxonomy ? taxonomy.stackType : undefined,
    resourceType:
      taxonomy && 'resourceType' in taxonomy
        ? taxonomy.resourceType
        : undefined,
    operation: taxonomy?.operation,
    familyId: family.id,
    familyLabel: family.label,
  };
};

export type TemplateFamily = {
  id: string;
  label: string;
  templates: TemplateView[];
};

/** Every Template entity the signed-in user is allowed to order. */
export const useTemplates = (): AsyncState<TemplateView[]> => {
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<AsyncState<TemplateView[]>>({
    loading: true,
    value: [],
  });

  useEffect(() => {
    let active = true;
    catalogApi
      .getEntities({ filter: { kind: 'Template' } })
      .then(res => {
        if (!active) return;
        // Tenant is the tiebreak, not decoration: the servings of one template
        // are identical apart from it, so sorting by title alone leaves their
        // order down to whatever the catalog happened to return and the same
        // menu can list acme above contoso one minute and below it the next.
        // Name breaks the remaining tie, because a title is not unique either -
        // nothing stops two templates in one tenant from sharing one.
        const items = res.items
          .map(toTemplateView)
          .sort(
            (a, b) =>
              a.title.localeCompare(b.title) ||
              (a.tenant ?? '').localeCompare(b.tenant ?? '') ||
              a.name.localeCompare(b.name),
          );
        setState({ loading: false, value: items });
      })
      .catch(error => {
        if (active) setState({ loading: false, error, value: [] });
      });
    return () => {
      active = false;
    };
  }, [catalogApi]);

  return state;
};

/**
 * Orderable templates grouped by family, alphabetically by label.
 *
 * Alphabetical rather than "most useful first": the menu is scanned, not read,
 * and there is no honest basis for ranking families for a user the platform
 * knows nothing about yet. "Other" is pinned last because it is a fallback,
 * not a category. Either way the order is stable, so adding a template never
 * moves the entry somebody's hand already knows.
 */
export const useTemplateFamilies = (
  templates: TemplateView[],
): TemplateFamily[] =>
  useMemo(() => {
    const byFamily = new Map<string, TemplateFamily>();

    for (const template of templates) {
      if (template.isDayTwo) continue;
      const family = byFamily.get(template.familyId) ?? {
        id: template.familyId,
        label: template.familyLabel,
        templates: [],
      };
      family.templates.push(template);
      byFamily.set(template.familyId, family);
    }

    return [...byFamily.values()].sort((a, b) => {
      if (a.id === OTHER_FAMILY.id) return 1;
      if (b.id === OTHER_FAMILY.id) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [templates]);

/** Templates a user can order, i.e. everything that is not a day-2 action. */
export const orderable = (templates: TemplateView[]) =>
  templates.filter(t => !t.isDayTwo);

/**
 * The tenants represented in a set of templates, alphabetically.
 *
 * Read by the menus to decide whether the tenant has to be named at all. One
 * tenant means naming it says nothing the user does not already know and only
 * repeats the same word down the list; two or more means the difference between
 * otherwise identical rows is invisible without it. Entitlement is not asked
 * for separately - a tenant the user cannot order in has no template here, so
 * what the catalog returned already is the answer.
 */
export const tenantsOf = (templates: TemplateView[]): string[] =>
  [
    ...new Set(
      templates
        .map(template => template.tenant)
        .filter((tenant): tenant is string => Boolean(tenant)),
    ),
  ].sort();

/**
 * Whether a list of templates has to name the tenant on each entry.
 *
 * The rule itself rather than each surface's reading of it, so the Provision
 * menu and the template picker cannot come to disagree about when a user is
 * shown a tenant.
 */
export const shouldShowTenant = (templates: TemplateView[]): boolean =>
  tenantsOf(templates).length > 1;
